import { ChevronRight, Shield } from 'lucide-react';

import type { RanqueDoTenantResponse } from '@/api/generated/model';
import { cn } from '@/shared/lib/cn';

/**
 * Gradiente do escudo por nível.
 *
 * ⚠️ Não é enfeite: o mock tinha prata fixo no código, e um Diamante veria a
 * mesma joia de quem é Prata — o card inteiro existe para dizer em que nível a
 * clínica está, e a peça mais visível estaria mentindo.
 *
 * Hex fixo em vez de token semântico de propósito: aqui a cor É o significado
 * (prata é prata, ouro é ouro), não a hierarquia visual da interface. Trocar o
 * tema não pode virar Ouro em cinza.
 */
const CORES_DE_RANQUE: Record<number, string> = {
  1: 'from-amber-700 to-amber-900',
  2: 'from-slate-300 to-slate-500',
  3: 'from-amber-300 to-amber-500',
  4: 'from-cyan-200 to-slate-400',
  5: 'from-violet-300 to-indigo-500',
};

/**
 * Escada fixa do Clube, em inglês (ajuste de Ago/2026: Standard, Silver, Gold,
 * Platinum, Diamond — pedido explícito, para não ficar preso ao rótulo que o
 * CRM manda).
 *
 * ⚠️ Fonte de verdade da EXIBIÇÃO. `ranque.rotulo`/`proximoRotulo` (vindos da
 * API) continuam em português — são o nome que o CRM/o fallback do backend
 * usam internamente, não o que a tela mostra. `nomeDoNivel()` traduz pelo
 * NÚMERO do nível, nunca pela string: assim a tela não depende de o texto do
 * CRM bater exatamente com "Prata"/"Ouro"/etc.
 */
const NIVEIS: readonly [nivel: number, nome: string][] = [
  [1, 'Standard'],
  [2, 'Silver'],
  [3, 'Gold'],
  [4, 'Platinum'],
  [5, 'Diamond'],
];

function nomeDoNivel(nivel: number | null): string | null {
  if (nivel === null) return null;
  return NIVEIS.find(([n]) => n === nivel)?.[1] ?? null;
}

/**
 * Card do nível do cliente no Clube (F5-6).
 *
 * ⚠️ Usa `bg-gradient-hormonalle`, e não `bg-surface-raised` como o mock: o
 * Clube é conteúdo da marca HORMONALLE, não da NOHRA Clinic — é a mesma regra
 * já aplicada ao atalho do dashboard, e o `tailwind.config.ts` nomeia este card
 * como o uso do token. Por isso todo o texto aqui é `text-white`, e não
 * `text-foreground`.
 *
 * ⚠️ Estilo "seletor de rank de jogo" (ajuste de Ago/2026): o nível em si vem
 * pronto do CRM, sem posição fracionária — por isso a escada mostra a
 * POSIÇÃO (anterior/atual/próximo) em vez de uma barra contínua.
 *
 * A barra de PONTOS logo abaixo é outra história: `ranque.pontos` só existe
 * quando o CRM manda esse campo na consulta — e hoje ele não manda. Por isso
 * `BarraDePontos` some inteira quando `pontos` é `null`, em vez de inventar um
 * "faltam X" que a gente não sabe se é verdade.
 */
export function CardDeRanque({ ranque }: { ranque: RanqueDoTenantResponse }) {
  const semNivel = ranque.ranque === 0;

  return (
    <div className="bg-gradient-hormonalle rounded-xl border-2 border-brand/20 p-6 space-y-5">
      <div className="flex flex-col items-center text-center gap-1">
        {/* ⚠️ O estado ZERO não existia no mock, e será o mais comum no
            primeiro dia: quem acabou de se cadastrar, ou cujo CRMV ainda não
            casou com nenhuma clínica do CRM. "Você é Cliente null" seria o
            resultado de reaproveitar a mesma frase. */}
        {semNivel ? (
          <>
            <p className="text-lg font-bold text-white leading-tight">
              Você ainda não está em um nível do Clube
            </p>
            <p className="text-sm text-white/80">
              Seu nível vem do seu histórico com a Hormonalle e é atualizado automaticamente.
            </p>
          </>
        ) : (
          <p className="text-lg font-bold text-white">
            Você é Cliente{' '}
            <span className="text-white">{nomeDoNivel(ranque.ranque) ?? ranque.rotulo}</span>
          </p>
        )}
      </div>

      {!semNivel && (
        <>
          <EscadaDeClassificacao ranqueAtual={ranque.ranque} />
          <BarraDePontos ranque={ranque} />
        </>
      )}

      {/* ⚠️ **Texto fixo, de propósito** (ajuste de Ago/2026). Antes este bloco
          contava os cupons visíveis; o programa de benefícios está sendo
          redesenhado e a contagem prometia uma vantagem que o nível ainda não
          entrega. Quando houver benefício de verdade por nível, é aqui que ele
          entra — e `totalDeCupons` volta a ter uso. */}
      <div className="w-full rounded-lg p-4 border border-white/20 bg-white/10 backdrop-blur-sm">
        <p className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-1">
          Seus benefícios
        </p>
        <p className="text-sm font-medium text-white">
          Novos benefícios serão disponibilizados em breve.
        </p>
        {ranque.importadoEm && (
          <p className="mt-2 text-[11px] text-white/60">
            Nível atualizado em {formatarData(ranque.importadoEm)}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Teto da escada PÚBLICA: Platinum. Diamond (5) é por convite exclusivo e fica
 * fora da escada — não aparece como "próximo" para quem está em Platinum, nem
 * como nível a alcançar. Um cliente Diamond ainda vê o próprio emblema como
 * ATUAL; o que some é o Diamond como alvo de progressão.
 */
const TETO_PUBLICO = 4;

/**
 * Anterior — ATUAL (grande) — próximo, sempre centralizado. Os dois lados
 * reservam a mesma largura mesmo quando vazios (Standard não tem anterior,
 * Platinum não tem próximo) — é o que mantém o emblema atual no centro do
 * card em vez de deslocar pra um lado nas pontas da escada.
 */
function EscadaDeClassificacao({ ranqueAtual }: { ranqueAtual: number }) {
  const indice = NIVEIS.findIndex(([nivel]) => nivel === ranqueAtual);
  if (indice === -1) return null;

  const anterior = indice > 0 ? (NIVEIS[indice - 1] ?? null) : null;
  const proximo = ranqueAtual < TETO_PUBLICO ? (NIVEIS[indice + 1] ?? null) : null;
  const nivelAtual = NIVEIS[indice];
  if (!nivelAtual) return null;
  const [, nomeAtual] = nivelAtual;

  return (
    <div className="flex items-center justify-center gap-2 sm:gap-5" aria-hidden="true">
      <EmblemaLateral nivel={anterior} />
      <ChevronRight className={cn('w-4 h-4 text-white/30 shrink-0', !anterior && 'invisible')} />

      <div className="flex flex-col items-center gap-2 shrink-0">
        <div
          className={`w-20 h-20 rounded-full bg-gradient-to-br ${CORES_DE_RANQUE[ranqueAtual]} flex items-center justify-center shadow-xl ring-4 ring-white/50`}
        >
          <Shield className="w-10 h-10 text-white" fill="currentColor" />
        </div>
        <span className="text-sm font-bold text-white whitespace-nowrap">{nomeAtual}</span>
      </div>

      <ChevronRight className={cn('w-4 h-4 text-white/30 shrink-0', !proximo && 'invisible')} />
      <EmblemaLateral nivel={proximo} />
    </div>
  );
}

/**
 * Pontos de faturamento + "faltam X para {próximo}", logo abaixo do emblema.
 *
 * ⚠️ Some inteira sem `pontos` — hoje é sempre o caso, até o CRM mandar o
 * campo (ver o pedido combinado com o time deles). Nada aqui é aproximado:
 * ou o número vem do backend, ou a barra não aparece.
 */
function BarraDePontos({ ranque }: { ranque: RanqueDoTenantResponse }) {
  // `?? null` também cobre a resposta vir sem a chave — o cliente HTTP tipa
  // como sempre presente, mas nada garante isso em runtime.
  const pontos = ranque.pontos ?? null;
  const pontosNivelAtual = ranque.pontosNivelAtual ?? null;
  const pontosProximoNivel = ranque.pontosProximoNivel ?? null;
  const proximoRanque = ranque.proximoRanque ?? null;
  const nomeDoProximo = nomeDoNivel(proximoRanque) ?? ranque.proximoRotulo;
  if (pontos === null) return null;

  const formatado = pontos.toLocaleString('pt-BR');

  // Sem piso do próximo nível pra calcular contra: Platinum (teto da escada
  // pública) ou Diamond (topo). Nos dois casos, só a contagem de pontos — sem
  // barra e sem mencionar o Diamond, que é convite exclusivo e fora da escada.
  if (
    pontosNivelAtual === null ||
    pontosProximoNivel === null ||
    proximoRanque === null ||
    proximoRanque > TETO_PUBLICO
  ) {
    return (
      <div className="text-center">
        <p className="text-sm font-semibold text-white">{formatado} pontos</p>
      </div>
    );
  }

  const percentual = Math.min(
    100,
    Math.max(0, ((pontos - pontosNivelAtual) / (pontosProximoNivel - pontosNivelAtual)) * 100),
  );
  const faltam = Math.max(0, pontosProximoNivel - pontos).toLocaleString('pt-BR');

  return (
    <div className="w-full max-w-sm mx-auto">
      <div className="h-2 rounded-full bg-white/20 overflow-hidden">
        <div
          className="h-full rounded-full bg-white transition-all"
          style={{ width: `${percentual}%` }}
        />
      </div>
      <div className="mt-1.5 flex items-center justify-between gap-2 text-[11px] text-white/70">
        <span className="font-semibold text-white whitespace-nowrap">{formatado} pontos</span>
        <span className="whitespace-nowrap">
          Faltam {faltam} para {nomeDoProximo}
        </span>
      </div>
    </div>
  );
}

function EmblemaLateral({ nivel }: { nivel: readonly [number, string] | null }) {
  return (
    <div className={cn('flex flex-col items-center gap-1.5 w-14 shrink-0', !nivel && 'invisible')}>
      {nivel && (
        <>
          <div
            className={`w-10 h-10 rounded-full bg-gradient-to-br ${CORES_DE_RANQUE[nivel[0]]} flex items-center justify-center opacity-45`}
          >
            <Shield className="w-5 h-5 text-white" fill="currentColor" />
          </div>
          <span className="text-[11px] font-medium text-white/50 whitespace-nowrap">{nivel[1]}</span>
        </>
      )}
    </div>
  );
}

function formatarData(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
}
